import type { FeatureModule, FeatureState } from '../types';
import { getCurrentWidgetLang, t } from '../i18n/index';

export default function createPageStructureModule(): FeatureModule {
  let enabled = false;
  let panelEl: HTMLElement | null = null;
  const PANEL_ID = 'accessify-page-structure';
  let mutationObserver: MutationObserver | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let originalPushState: typeof history.pushState | null = null;
  let originalReplaceState: typeof history.replaceState | null = null;
  const boundPopstateHandler = () => scheduleRefresh();
  let lastContentHash = '';

  function lang(): string {
    return getCurrentWidgetLang();
  }

  function collectHeadings(): Array<{ tag: string; text: string; el: HTMLElement }> {
    const results: Array<{ tag: string; text: string; el: HTMLElement }> = [];
    const headings = document.querySelectorAll<HTMLElement>('h1, h2, h3, h4, h5, h6');
    headings.forEach((el) => {
      if (el.closest('#accessify-root')) return;
      const text = el.textContent?.trim() || '';
      if (text) results.push({ tag: el.tagName.toLowerCase(), text, el });
    });
    return results;
  }

  function collectLandmarks(): Array<{ role: string; label: string; el: HTMLElement }> {
    const results: Array<{ role: string; label: string; el: HTMLElement }> = [];
    const selectors = [
      'main', 'nav', 'aside', 'header', 'footer', 'section[aria-label]',
      'section[aria-labelledby]', '[role="main"]', '[role="navigation"]',
      '[role="complementary"]', '[role="banner"]', '[role="contentinfo"]',
      '[role="search"]',
    ];
    const els = document.querySelectorAll<HTMLElement>(selectors.join(','));
    els.forEach((el) => {
      if (el.closest('#accessify-root')) return;
      const role = el.getAttribute('role') || el.tagName.toLowerCase();
      const label = el.getAttribute('aria-label') || el.getAttribute('aria-labelledby') || role;
      results.push({ role, label, el });
    });
    return results;
  }

  function buildPanel() {
    panelEl = document.createElement('div');
    panelEl.id = PANEL_ID;
    panelEl.setAttribute('role', 'dialog');
    panelEl.setAttribute('aria-label', t('pageStructure.title', lang()));

    const headings = collectHeadings();
    const landmarks = collectLandmarks();

    const indent: Record<string, string> = {
      h1: '0', h2: '12px', h3: '24px', h4: '36px', h5: '48px', h6: '60px',
    };

    let html = `
      <style>
        #${PANEL_ID} {
          position: fixed; top: 50%; left: 50%;
          transform: translate(-50%, -50%);
          z-index: 999998;
          background: #fff; color: #1a1a1a;
          border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.2);
          padding: 24px; width: 400px; max-width: calc(100vw - 32px); max-height: 80vh;
          overflow-y: auto; font-family: system-ui, sans-serif;
          font-size: 14px; line-height: 1.5;
        }
        #${PANEL_ID} h3 { margin: 0 0 12px; font-size: 16px; }
        #${PANEL_ID} .ps-section { margin-bottom: 16px; }
        #${PANEL_ID} .ps-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: #888; margin-bottom: 6px; }
        #${PANEL_ID} .ps-item {
          display: block; width: 100%; text-align: left;
          padding: 6px 8px; border: none; background: none;
          cursor: pointer; border-radius: 6px; font-size: 13px;
          color: #1a1a1a; font-family: inherit;
        }
        #${PANEL_ID} .ps-item:hover { background: #f0f0f2; }
        #${PANEL_ID} .ps-item:focus-visible { outline: 2px solid #0055CC; outline-offset: 1px; }
        #${PANEL_ID} .ps-tag { color: #888; font-size: 11px; margin-right: 6px; }
        #${PANEL_ID} .ps-close {
          position: absolute; top: 12px; right: 12px;
          background: none; border: none; cursor: pointer;
          font-size: 18px; color: #888; padding: 4px 8px; border-radius: 4px;
        }
        #${PANEL_ID} .ps-close:hover { background: #f0f0f2; }
        @media (prefers-color-scheme: dark) {
          #${PANEL_ID} { background: #1a1a1a; color: #f0f0f0; }
          #${PANEL_ID} .ps-item { color: #f0f0f0; }
          #${PANEL_ID} .ps-item:hover { background: #2a2a2e; }
          #${PANEL_ID} .ps-close { color: #aaa; }
          #${PANEL_ID} .ps-close:hover { background: #2a2a2e; }
        }
      </style>
      <button class="ps-close" aria-label="${t('widget.close', lang())}">&times;</button>
      <h3>${t('pageStructure.title', lang())}</h3>
    `;

    if (headings.length > 0) {
      html += `<div class="ps-section"><div class="ps-label">${t('pageStructure.headings', lang())}</div>`;
      headings.forEach((h, i) => {
        html += `<button class="ps-item" data-type="heading" data-index="${i}" style="padding-left:${indent[h.tag] || '0'}">
          <span class="ps-tag">${h.tag}</span>${escapeHtml(h.text.slice(0, 80))}
        </button>`;
      });
      html += '</div>';
    }

    if (landmarks.length > 0) {
      html += `<div class="ps-section"><div class="ps-label">${t('pageStructure.landmarks', lang())}</div>`;
      landmarks.forEach((lm, i) => {
        html += `<button class="ps-item" data-type="landmark" data-index="${i}">
          <span class="ps-tag">${escapeHtml(lm.role)}</span>${escapeHtml(lm.label.slice(0, 60))}
        </button>`;
      });
      html += '</div>';
    }

    if (headings.length === 0 && landmarks.length === 0) {
      html += `<p style="color:#888">${t('pageStructure.empty', lang())}</p>`;
    }

    panelEl.innerHTML = html;

    // Event delegation
    panelEl.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('.ps-item');
      if (btn) {
        const type = btn.dataset.type;
        const idx = parseInt(btn.dataset.index || '0', 10);
        let target: HTMLElement | undefined;
        if (type === 'heading') target = headings[idx]?.el;
        else if (type === 'landmark') target = landmarks[idx]?.el;
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          target.focus({ preventScroll: true });
        }
      }
      if ((e.target as HTMLElement).closest('.ps-close')) {
        deactivate();
      }
    });

    document.body.appendChild(panelEl);
    const firstBtn = panelEl.querySelector<HTMLElement>('.ps-item, .ps-close');
    firstBtn?.focus();
  }

  function escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // --- Content hash for change detection ---
  function contentHash(): string {
    const headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    let hash = '';
    headings.forEach((el) => { hash += el.textContent?.trim().slice(0, 20) || ''; });
    return hash;
  }

  function scheduleRefresh() {
    if (!enabled || !panelEl) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const newHash = contentHash();
      if (newHash !== lastContentHash) {
        lastContentHash = newHash;
        panelEl?.remove();
        buildPanel();
      }
    }, 300);
  }

  // --- SPA navigation detection ---
  function setupSPAListeners() {
    // Override pushState/replaceState to detect SPA navigation
    if (!originalPushState) {
      originalPushState = history.pushState;
      history.pushState = function (...args: Parameters<typeof history.pushState>) {
        originalPushState!.apply(this, args);
        scheduleRefresh();
      };
    }
    if (!originalReplaceState) {
      originalReplaceState = history.replaceState;
      history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
        originalReplaceState!.apply(this, args);
        scheduleRefresh();
      };
    }
    window.addEventListener('popstate', boundPopstateHandler);
  }

  function teardownSPAListeners() {
    if (originalPushState) {
      history.pushState = originalPushState;
      originalPushState = null;
    }
    if (originalReplaceState) {
      history.replaceState = originalReplaceState;
      originalReplaceState = null;
    }
    window.removeEventListener('popstate', boundPopstateHandler);
  }

  // --- MutationObserver for dynamic content ---
  function setupObserver() {
    if (mutationObserver) return;
    mutationObserver = new MutationObserver(() => scheduleRefresh());
    const target = document.querySelector('main') || document.body;
    mutationObserver.observe(target, { childList: true, subtree: true });
  }

  function teardownObserver() {
    mutationObserver?.disconnect();
    mutationObserver = null;
    if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null; }
  }

  function activate() {
    enabled = true;
    lastContentHash = contentHash();
    buildPanel();
    setupObserver();
    setupSPAListeners();
  }

  function deactivate() {
    enabled = false;
    teardownObserver();
    teardownSPAListeners();
    panelEl?.remove();
    panelEl = null;
  }

  return {
    id: 'page-structure',
    name: () => 'Page Structure',
    description: 'View headings and landmarks for quick navigation',
    icon: 'page-structure',
    category: 'motor',
    activate,
    deactivate,
    getState: (): FeatureState => ({ id: 'page-structure', enabled }),
  };
}
